import { faComment, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import { faExternalLink } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import { blogsUrl } from "@/mk2/apps/blogs/urls";
import { HomePostEntity, HomePostType } from "@/mk2/apps/home/schemas";
import { Img } from "@/mk2/components/Img";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { StarsRating } from "@/mk2/components/StarsRating";
import { getDomain } from "@/mk2/helpers/urls";
import {
    HOMEPAGE_POST_TYPE_ARTICLE,
    HOMEPAGE_POST_TYPE_BLOG,
    HOMEPAGE_POST_TYPE_CONTEST,
    HOMEPAGE_POST_TYPE_COUNSELLING,
    HOMEPAGE_POST_TYPE_COUNSELLING_TOPIC,
    HOMEPAGE_POST_TYPE_DISCUSSION,
    HOMEPAGE_POST_TYPE_EXPERIENCE,
    HOMEPAGE_POST_TYPE_FORUM_TOPIC,
    HOMEPAGE_POST_TYPE_FROM_INTERNET,
    HOMEPAGE_POST_TYPE_PHOTOBLOG_ALBUM,
    HOMEPAGE_POST_TYPE_PHOTOBLOG_ARTICLE,
    HOMEPAGE_POST_TYPE_REVIEW,
    HOMEPAGE_POST_TYPE_REVIEW_ARTICLE,
    HOMEPAGE_POST_TYPE_SHORT_MESSAGE,
    HOMEPAGE_POST_TYPE_YOUTUBE_MK,
} from "@/mk/autogenerated/translation";
import { decidePreviewPhotoVersion, photoUrl } from "@/mk/photo/photoUrl";

import styles from "./Post.mscss";

interface HeadingOwnProps {
    post: HomePostEntity;
}

const getPostTypeLabel = (post: HomePostEntity): React.ReactNode => {
    if (
        (post.type === HomePostType.SHORT_MESSAGE ||
            post.type === HomePostType.PHOTOBLOG_ARTICLE) &&
        post.user?.username
    ) {
        return (
            <>
                {HOMEPAGE_POST_TYPE_BLOG} &gt;&nbsp;
                <div>@{post.user.username}</div>
            </>
        );
    }

    if (post.type === HomePostType.DISCUSSION && post.topicName && post.topicSlug) {
        return (
            <>
                {HOMEPAGE_POST_TYPE_DISCUSSION} &gt;&nbsp;
                <div>{post.topicName}</div>
            </>
        );
    }

    if (post.type === HomePostType.WIKI_EXPERIENCE && post.starsCount > 0) {
        return (
            <>
                {HOMEPAGE_POST_TYPE_EXPERIENCE}&nbsp;
                <StarsRating starsCount={post.starsCount} className={styles.Post__starsRating} />
            </>
        );
    }

    return typeTranslations[post.type];
};

let PostHeading: React.FunctionComponent<HeadingOwnProps> = ({ post }) => (
    <>
        <div className={styles.Post__typeAndStats}>
            <div className={styles.Post__type}>
                {post.label || (
                    <>
                        {getPostTypeLabel(post)}
                        {post.type === HomePostType.FROM_INTERNET && (
                            <>
                                &nbsp;
                                <span>{getDomain(post.url)}</span>
                            </>
                        )}
                    </>
                )}
            </div>
            <div className={styles.Post__stats}>
                {post.likes > 0 && (
                    <span className={styles.Post__stat}>
                        {post.likes} <FontAwesomeIcon icon={faThumbsUp} />
                    </span>
                )}
                {post.comments > 0 && (
                    <span className={styles.Post__stat}>
                        {post.comments} <FontAwesomeIcon icon={faComment} />
                    </span>
                )}
            </div>
        </div>
        {post.type === HomePostType.WIKI_EXPERIENCE && post.parentWikiArticleTitle ? (
            <>
                <h2 className={styles.Post__title}>
                    <span className={styles.Post__titleClamp}>{post.title}</span>
                </h2>
                <div className={styles.Post__description}>
                    {post.parentWikiArticleTitle.charAt(0).toUpperCase() +
                        post.parentWikiArticleTitle.slice(1)}
                </div>
            </>
        ) : (
            <h2 className={styles.Post__title}>
                <span className={styles.Post__titleClamp}>
                    {post.title || post.description}
                    {post.type === HomePostType.FROM_INTERNET && (
                        <>
                            &nbsp;
                            <FontAwesomeIcon icon={faExternalLink} />
                        </>
                    )}
                </span>
            </h2>
        )}
    </>
);

PostHeading.displayName = "PostHeading";

PostHeading = React.memo<HeadingOwnProps>(PostHeading);

interface OwnProps {
    className?: string;
    style?: React.CSSProperties;
    post: HomePostEntity;

    onClick?(event: React.MouseEvent<HTMLAnchorElement>);
}

type Props = OwnProps;

const typeTranslations = {
    // YOUTUBE, WEB (AJ MK ak tinyurl, sutaze)
    [HomePostType.YOUTUBE_MK]: HOMEPAGE_POST_TYPE_YOUTUBE_MK,
    [HomePostType.FROM_INTERNET]: HOMEPAGE_POST_TYPE_FROM_INTERNET,

    // BLOGY
    [HomePostType.PHOTOBLOG_ALBUM]: HOMEPAGE_POST_TYPE_PHOTOBLOG_ALBUM,
    [HomePostType.PHOTOBLOG_ARTICLE]: HOMEPAGE_POST_TYPE_PHOTOBLOG_ARTICLE,
    [HomePostType.SHORT_MESSAGE]: HOMEPAGE_POST_TYPE_SHORT_MESSAGE,

    // PRE MNA - FORUM / BLOGY / PORADNE

    // PORADNE
    [HomePostType.COUNSELLING]: HOMEPAGE_POST_TYPE_COUNSELLING,
    [HomePostType.COUNSELLING_TOPIC]: HOMEPAGE_POST_TYPE_COUNSELLING_TOPIC,
    [HomePostType.COUNSELLING_COMMENT]: HOMEPAGE_POST_TYPE_COUNSELLING_TOPIC,

    // SUTAZE
    [HomePostType.CONTEST]: HOMEPAGE_POST_TYPE_CONTEST,

    // FORUM
    [HomePostType.FORUM_TOPIC]: HOMEPAGE_POST_TYPE_FORUM_TOPIC,
    [HomePostType.DISCUSSION]: HOMEPAGE_POST_TYPE_DISCUSSION,
    [HomePostType.FORUM_COMMENT]: HOMEPAGE_POST_TYPE_FORUM_TOPIC,

    // WIKI
    [HomePostType.WIKI_ARTICLE]: HOMEPAGE_POST_TYPE_ARTICLE,

    // RECENZIE
    [HomePostType.WIKI_REVIEW_ARTICLE]: HOMEPAGE_POST_TYPE_REVIEW_ARTICLE,
    [HomePostType.WIKI_EXPERIENCE]: HOMEPAGE_POST_TYPE_EXPERIENCE,

    // KOCIKY - asi unused
    [HomePostType.STROLLER_USER_REVIEW]: HOMEPAGE_POST_TYPE_REVIEW,

    // TBD: RECEPTY, SKUPINY
};

const BLOG_POST_TYPES = new Set([HomePostType.SHORT_MESSAGE, HomePostType.PHOTOBLOG_ALBUM]);

const COUNSELLING_POST_TYPES = new Set([
    HomePostType.COUNSELLING,
    HomePostType.COUNSELLING_TOPIC,
    HomePostType.COUNSELLING_COMMENT,
]);

const getPostHref = (post: HomePostEntity): string => {
    if (BLOG_POST_TYPES.has(post.type) && post.objectId) {
        return `${blogsUrl}?hp=${post.objectId}`;
    }
    return post.url;
};

const Post: React.FunctionComponent<Props> = ({ className, post, style }) => {
    const photo = COUNSELLING_POST_TYPES.has(post.type) ? post.counsellor?.avatarPhoto : post.photo;
    return (
        <Link
            to={getPostHref(post)}
            className={cx(styles.Post, className)}
            style={style}
            target={post.targetBlank ? "_blank" : undefined}
            data-cy="home-post"
        >
            <div className={styles.Post__content}>
                <div className={styles.Post__image}>
                    {photo?.code ? (
                        <Img
                            className={styles.Post__image__img}
                            src={photoUrl(photo, decidePreviewPhotoVersion("c300x300", photo))}
                            width={80}
                            height={80}
                            alt={post.title || post.description || ""}
                        />
                    ) : null}
                </div>
                <div key={post.id} className={styles.Post__rightColumn}>
                    <PostHeading post={post} />
                </div>
            </div>
        </Link>
    );
};

Post.displayName = "Post";

export default React.memo<Props>(Post);

// export default React.memo<Props>(Post);

interface PlaceholderOwnProps {
    className?: string;
    style?: React.CSSProperties;
}

let PostPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({ className, style }) => (
    <div className={cx(styles.Post, className)} style={style} data-cy="home-post-placeholder">
        <div className={styles.Post__content}>
            <div className={styles.Post__image}>
                <LoadingPlaceholder width={80} height={80} />
            </div>
            <div className={styles.Post__rightColumn}>
                <div className={styles.Post__type}>
                    <LoadingPlaceholder width={80} height={18} />
                </div>
                <span className={styles.Post__title}>
                    <LoadingPlaceholder width={200} height={18} />
                </span>
            </div>
        </div>
    </div>
);

PostPlaceholder.displayName = "PostPlaceholder";

PostPlaceholder = React.memo<PlaceholderOwnProps>(PostPlaceholder);

export { PostPlaceholder };

export function outerHeightMax(isMobile: boolean): number {
    // Príklad: výška postu na mobile je menšia ako na desktope
    return isMobile ? 110 : 100;
}
